import { Component, ojs, app, state, v, payload } from "modular-openscriptjs";
import {
    pageInfo,
    scrollToTop,
    either,
    isMobile,
    cleanDispose,
    inform,
    updateStateValue,
    monetary,
    consoleErrorOnLocal,
    formatDateForHumans,
} from "../../../helpers";
import { api, dc, STRINGS } from "../../../declarations";
import { routes } from "../../../backendRoutes";
import { $e } from "../../../ojs.events";
import { dom, tool } from "../../../fotastart";
import SolidIcon from "../../Blocks/SolidIcon";
import OffcanvasBody from "../../Offcanvas/OffcanvasBody";
import ModalBody from "../../Modals/ModalBody";
import Spinner, { SmallSpinner } from "../../Blocks/Spinner";

const h = app("h");
const router = app("router");
const broker = app("broker");

ojs(SolidIcon, OffcanvasBody, ModalBody, Spinner, SmallSpinner);

export default class ViewDeals extends Component {
    companyId = null;

    // --- List state ---
    deals = state([]);
    loadingDeals = state(true);
    // status: "all" | "active" | "inactive"
    filters = state({
        status: "all",
        startDate: "",
        endDate: "",
    });

    // --- Deal create/edit form state ---
    dealFormHolder = null;
    editingDealId = null;
    formData = {
        name: "",
        type: "fixed",
        value: "",
        starts_at: "",
        ends_at: "",
        active: true,
    };
    formType = state("fixed");
    formActive = state(true);
    dealProducts = state([]); // [{ id, name, thumbnail_path, selling_price }]

    productSearchQuery = state("");
    productSuggestions = state([]);
    loadingProductSuggestions = state(false);
    showProductSuggestions = state(false);
    productSearchTimeout = null;

    // Guards against re-opening the create form on every re-render when
    // reached via the "business.deals.new" route.
    _autoOpened = false;

    $$deal = {
        deleted: () => {
            this.fetchDeals();
        },
    };

    render() {
        this.companyId = router.params.companyId;
        const businessName = window.currentCompany?.name || "Your Business";

        pageInfo(`View Deals - ${businessName}`);
        scrollToTop();

        if (this.deals.value.length === 0 && this.loadingDeals.value) {
            this.fetchDeals();
        }

        // Reached via "New Deal" quick-action card - pop the create form
        // automatically, once.
        if (router.is("business.deals.new") && !this._autoOpened) {
            this._autoOpened = true;
            setTimeout(() => this.openDealForm(), 0);
        }

        return h.div(
            { class: "min-vh-100 py-5 mb-5 py-md-5" },
            h.div(
                { class: "container" },
                // Header
                h.div(
                    {
                        class: "d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4",
                    },
                    h.div(
                        { class: "d-flex align-items-center gap-3" },
                        h.button(
                            {
                                class: "btn btn-white border shadow-sm rounded-circle p-2",
                                listeners: { click: () => history.back() },
                            },
                            h.SolidIcon("arrow-left"),
                        ),
                        h.div(
                            {},
                            h.h2({ class: "fw-bold mb-0" }, "View Deals"),
                            h.p(
                                { class: "text-muted mb-0 small" },
                                "Browse, filter, and manage your existing deals.",
                            ),
                        ),
                    ),
                    h.button(
                        {
                            class: "btn btn-primary rounded-pill px-4 fw-bold d-flex align-items-center gap-2",
                            listeners: { click: () => this.openDealForm() },
                        },
                        h.i({ class: "bi bi-plus-lg" }),
                        "New Deal",
                    ),
                ),

                this.renderDealsList(),
            ),
        );
    }

    // =====================================================================
    // LIST
    // =====================================================================

    renderDealsList() {
        return h.div(
            {},
            // Filter bar
            h.div(
                { class: "card border-0 shadow-sm rounded-4 mb-4" },
                h.div(
                    { class: "card-body p-3 p-md-4" },
                    h.div(
                        { class: "row g-3 align-items-end" },
                        h.div(
                            { class: "col-12 col-md-3" },
                            h.label(
                                {
                                    class: "form-label small fw-bold text-muted text-uppercase d-block",
                                },
                                "Status",
                            ),
                            v(this.filters, (filtersState) => {
                                const current = filtersState.value.status;
                                const options = [
                                    ["all", "All"],
                                    ["active", "Active"],
                                    ["inactive", "Inactive"],
                                ];
                                return h.div(
                                    {
                                        class: "btn-group w-100",
                                        role: "group",
                                    },
                                    ...options.map(([value, label]) =>
                                        h.button(
                                            {
                                                type: "button",
                                                class: `btn btn-sm fw-bold ${
                                                    current === value
                                                        ? "btn-dark"
                                                        : "btn-white border"
                                                }`,
                                                listeners: {
                                                    click: () =>
                                                        updateStateValue(
                                                            this.filters,
                                                            (f) => {
                                                                f.status =
                                                                    value;
                                                                return f;
                                                            },
                                                        ),
                                                },
                                            },
                                            label,
                                        ),
                                    ),
                                );
                            }),
                        ),
                        h.div(
                            { class: "col-6 col-md-3" },
                            h.label(
                                {
                                    class: "form-label small fw-bold text-muted text-uppercase",
                                },
                                "From",
                            ),
                            h.input({
                                type: "date",
                                class: "form-control rounded-3 border bg-light",
                                value: this.filters.value.startDate,
                                listeners: {
                                    change: (e) =>
                                        updateStateValue(this.filters, (f) => {
                                            f.startDate = e.target.value;
                                            return f;
                                        }),
                                },
                            }),
                        ),
                        h.div(
                            { class: "col-6 col-md-3" },
                            h.label(
                                {
                                    class: "form-label small fw-bold text-muted text-uppercase",
                                },
                                "To",
                            ),
                            h.input({
                                type: "date",
                                class: "form-control rounded-3 border bg-light",
                                value: this.filters.value.endDate,
                                listeners: {
                                    change: (e) =>
                                        updateStateValue(this.filters, (f) => {
                                            f.endDate = e.target.value;
                                            return f;
                                        }),
                                },
                            }),
                        ),
                        h.div(
                            { class: "col-12 col-md-3" },
                            h.button(
                                {
                                    class: "btn btn-light rounded-pill w-100 fw-bold",
                                    listeners: {
                                        click: () => {
                                            this.filters.value = {
                                                status: "all",
                                                startDate: "",
                                                endDate: "",
                                            };
                                        },
                                    },
                                },
                                "Reset Filters",
                            ),
                        ),
                    ),
                ),
            ),

            v(this.loadingDeals, (loadingState) => {
                if (loadingState.value) {
                    return h.div(
                        { class: "text-center py-5" },
                        h.Spinner(),
                    );
                }

                return v(this.deals, (dealsState) =>
                    v(this.filters, (filtersState) => {
                        const filtered = this.filterDeals(
                            dealsState.value,
                            filtersState.value,
                        );

                        if (filtered.length === 0) {
                            return h.div(
                                { class: "text-center py-5 text-muted" },
                                h.i({
                                    class: "bi bi-tags fs-1 d-block mb-3",
                                }),
                                dealsState.value.length === 0
                                    ? "No deals yet. Create your first deal to offer discounts on your products."
                                    : "No deals match the current filters.",
                            );
                        }

                        return h.div(
                            { class: "d-grid gap-3" },
                            ...filtered.map((deal) =>
                                this.renderDealCard(deal),
                            ),
                        );
                    }),
                );
            }),
        );
    }

    renderDealCard(deal) {
        const currentlyActive = this.isDealCurrentlyActive(deal);
        const productCount = deal.products?.length ?? 0;

        return h.div(
            {
                id: `deal-card-${deal.id}`,
                class: "card border-0 shadow-sm rounded-4",
            },
            h.div(
                {
                    class: "card-body p-4 d-flex flex-column flex-md-row gap-3 align-items-md-center justify-content-between",
                },
                h.div(
                    { class: "flex-fill" },
                    h.div(
                        {
                            class: "d-flex align-items-center gap-2 mb-1 flex-wrap",
                        },
                        h.h5({ class: "fw-bold mb-0" }, deal.name),
                        h.span(
                            {
                                class: `badge rounded-pill ${
                                    currentlyActive
                                        ? "bg-success-subtle text-success"
                                        : "bg-secondary-subtle text-secondary"
                                }`,
                            },
                            currentlyActive ? "Active" : "Inactive",
                        ),
                    ),
                    h.div(
                        { class: "text-primary fw-bold mb-1" },
                        this.formatDealValue(deal),
                    ),
                    h.div(
                        { class: "text-muted small mb-1" },
                        h.i({ class: "bi bi-calendar3 me-1" }),
                        this.formatDealWindow(deal),
                    ),
                    h.div(
                        { class: "text-muted small" },
                        h.i({ class: "bi bi-box-seam me-1" }),
                        `${productCount} product${productCount === 1 ? "" : "s"}`,
                    ),
                ),
                h.div(
                    {
                        class: "d-flex gap-2",
                        "data-deal-id": deal.id,
                    },
                    h.button(
                        {
                            id: `deal-report-btn-${deal.id}`,
                            class: "btn btn-white btn-sm border shadow-sm rounded-circle",
                            title: "View Report",
                            "data-deal-id": deal.id,
                            listeners: {
                                click: () =>
                                    router.to("business.deals.reports.deal", {
                                        companyId: this.companyId,
                                        dealId: deal.id,
                                    }),
                            },
                        },
                        h.i({ class: "bi bi-graph-up" }),
                    ),
                    h.button(
                        {
                            id: `deal-edit-btn-${deal.id}`,
                            class: "btn btn-white btn-sm border shadow-sm rounded-circle",
                            title: "Edit",
                            "data-deal-id": deal.id,
                            listeners: {
                                click: () => this.openDealForm(deal),
                            },
                        },
                        h.i({ class: "bi bi-pencil" }),
                    ),
                    h.button(
                        {
                            id: `deal-delete-btn-${deal.id}`,
                            class: "btn btn-white btn-sm border shadow-sm rounded-circle text-danger",
                            title: "Delete",
                            "data-deal-id": deal.id,
                            listeners: {
                                click: () => this.deleteDeal(deal),
                            },
                        },
                        h.i({ class: "bi bi-trash" }),
                    ),
                ),
            ),
        );
    }

    // =====================================================================
    // DEAL CREATE / EDIT FORM
    // =====================================================================

    openDealForm(deal = null) {
        this.editingDealId = deal ? deal.id : null;

        this.formData = {
            name: deal?.name ?? "",
            type: deal?.type ?? "fixed",
            value: deal?.value ?? "",
            starts_at: deal?.starts_at
                ? this.toDateInputValue(deal.starts_at)
                : "",
            ends_at: deal?.ends_at
                ? this.toDateInputValue(deal.ends_at)
                : "",
            active: deal ? !!deal.active : true,
        };

        this.formType.value = this.formData.type;
        this.formActive.value = this.formData.active;

        this.dealProducts.value = (deal?.products ?? []).map((p) => ({
            id: p.id,
            name: p.name,
            thumbnail_path: p.thumbnail_path,
            selling_price: p.selling_price,
        }));

        this.productSearchQuery.value = "";
        this.productSuggestions.value = [];
        this.showProductSuggestions.value = false;

        if (this.dealFormHolder) cleanDispose(this.dealFormHolder);

        const content = h.div(
            { class: "p-3" },
            h.div(
                {
                    class: "d-flex justify-content-between align-items-center mb-4",
                },
                h.h4(
                    { class: "fw-bold mb-0" },
                    deal ? "Edit Deal" : "New Deal",
                ),
                h.button({
                    class: "btn-close",
                    listeners: { click: () => this.dealFormHolder.hide() },
                }),
            ),
            this.renderDealForm(),
        );

        if (isMobile()) {
            this.dealFormHolder = tool.offcanvas(h.OffcanvasBody(content), {
                class: "rounded-top-5 min-vh-75",
            });
        } else {
            this.dealFormHolder = tool.plainModal(h.ModalBody(content), {
                class: "modal-lg",
            });
        }

        this.dealFormHolder.show();
    }

    renderDealForm() {
        return h.div(
            {},
            h.div(
                { class: "mb-3" },
                h.label(
                    {
                        class: "form-label fw-bold small text-muted text-uppercase mb-1",
                    },
                    "Deal Name",
                ),
                h.input({
                    class: "form-control bg-light border p-3 rounded-3",
                    type: "text",
                    value: this.formData.name,
                    placeholder: "e.g. Weekend Special",
                    listeners: {
                        input: (e) => (this.formData.name = e.target.value),
                    },
                }),
            ),

            h.div(
                { class: "mb-3" },
                h.label(
                    {
                        class: "form-label fw-bold small text-muted text-uppercase mb-1",
                    },
                    "Discount Type",
                ),
                h.select(
                    {
                        class: "form-select bg-light border p-3 rounded-3",
                        listeners: {
                            change: (e) => {
                                this.formData.type = e.target.value;
                                this.formType.value = e.target.value;
                            },
                        },
                    },
                    h.option(
                        { value: "fixed" },
                        either(this.formData.type === "fixed", {
                            selected: true,
                        }),
                        "Fixed Amount",
                    ),
                    h.option(
                        { value: "percentage" },
                        either(this.formData.type === "percentage", {
                            selected: true,
                        }),
                        "Percentage",
                    ),
                ),
            ),

            v(this.formType, (typeState) => {
                const isPercentage = typeState.value === "percentage";
                const inputAttrs = {
                    class: "form-control bg-light border p-3 rounded-3",
                    type: "number",
                    min: "0",
                    step: "0.01",
                    value: this.formData.value,
                    placeholder: isPercentage ? "e.g. 20" : "e.g. 500",
                    listeners: {
                        input: (e) => (this.formData.value = e.target.value),
                    },
                };
                if (isPercentage) inputAttrs.max = "100";

                return h.div(
                    { class: "mb-3" },
                    h.label(
                        {
                            class: "form-label fw-bold small text-muted text-uppercase mb-1",
                        },
                        isPercentage
                            ? "Discount Percentage (%)"
                            : `Discount Amount (${dc.userCurrency})`,
                    ),
                    h.input(inputAttrs),
                    isPercentage
                        ? h.small(
                              { class: "text-muted mt-1 d-block" },
                              "Cannot exceed 100%.",
                          )
                        : null,
                );
            }),

            h.div(
                { class: "row g-3 mb-3" },
                h.div(
                    { class: "col-6" },
                    h.label(
                        {
                            class: "form-label fw-bold small text-muted text-uppercase mb-1",
                        },
                        "Start Date",
                    ),
                    h.input({
                        type: "date",
                        class: "form-control bg-light border p-3 rounded-3",
                        value: this.formData.starts_at,
                        listeners: {
                            change: (e) =>
                                (this.formData.starts_at = e.target.value),
                        },
                    }),
                ),
                h.div(
                    { class: "col-6" },
                    h.label(
                        {
                            class: "form-label fw-bold small text-muted text-uppercase mb-1",
                        },
                        "End Date",
                    ),
                    h.input({
                        type: "date",
                        class: "form-control bg-light border p-3 rounded-3",
                        value: this.formData.ends_at,
                        listeners: {
                            change: (e) =>
                                (this.formData.ends_at = e.target.value),
                        },
                    }),
                ),
            ),

            h.div(
                { class: "mb-4 border rounded-4 p-3 bg-light" },
                this.renderActiveToggle(),
            ),

            this.renderDealProductPicker(),

            h.button(
                {
                    class: "btn btn-primary w-100 rounded-pill py-3 fw-bold mt-2",
                    id: "save-deal-btn",
                    listeners: { click: (e) => this.saveDeal(e.target) },
                },
                this.editingDealId ? "Update Deal" : "Create Deal",
            ),
        );
    }

    renderActiveToggle() {
        return h.div(
            {
                class: "d-flex justify-content-between align-items-center gap-3 py-1",
            },
            h.div(
                { class: "flex-fill" },
                h.h6({ class: "fw-bold mb-0 small" }, "Active?"),
                h.p(
                    { class: "text-muted smaller mb-0" },
                    "Inactive deals are hidden from customers.",
                ),
            ),
            h.div(
                { class: "btn-group p-1 bg-white rounded-pill border" },
                v(this.formActive, (val) =>
                    h.button(
                        {
                            class: `btn btn-xs rounded-pill py-1 px-3 fw-bold ${
                                !val.value
                                    ? "bg-danger text-white"
                                    : "bg-transparent text-muted border-0"
                            }`,
                            listeners: {
                                click: () => {
                                    this.formActive.value = false;
                                    this.formData.active = false;
                                },
                            },
                        },
                        "No",
                    ),
                ),
                v(this.formActive, (val) =>
                    h.button(
                        {
                            class: `btn btn-xs rounded-pill py-1 px-3 fw-bold ${
                                val.value
                                    ? "bg-success text-white"
                                    : "bg-transparent text-muted border-0"
                            }`,
                            listeners: {
                                click: () => {
                                    this.formActive.value = true;
                                    this.formData.active = true;
                                },
                            },
                        },
                        "Yes",
                    ),
                ),
            ),
        );
    }

    renderDealProductPicker() {
        return h.div(
            {},
            h.div(
                { class: "mb-2 position-relative" },
                h.label(
                    {
                        class: "form-label fw-bold small text-muted text-uppercase",
                    },
                    "Search and add products to this deal",
                ),
                h.input({
                    class: "form-control bg-light border p-3 rounded-3",
                    placeholder: "Search products...",
                    id: "deal-product-search",
                    listeners: {
                        input: (e) => this.handleDealProductSearch(e),
                        focus: () => {
                            if (this.productSuggestions.value.length > 0)
                                this.showProductSuggestions.value = true;
                        },
                    },
                }),
                v(this.showProductSuggestions, (show) => {
                    if (!show.value) return null;
                    return h.div(
                        {
                            class: "position-absolute w-100 bg-white shadow rounded-4 mt-2 overflow-hidden border",
                            style: "z-index: 1000;",
                        },
                        v(this.loadingProductSuggestions, (loading) => {
                            if (loading.value)
                                return h.div(
                                    { class: "p-3 text-center" },
                                    h.SmallSpinner(),
                                );
                            return v(this.productSuggestions, (sugs) => {
                                if (sugs.value.length === 0)
                                    return h.div(
                                        { class: "p-3 text-muted small" },
                                        "No products found.",
                                    );
                                return h.div(
                                    { class: "list-group list-group-flush" },
                                    ...sugs.value.map((prod) =>
                                        h.button(
                                            {
                                                class: "list-group-item list-group-item-action d-flex align-items-center gap-2 py-2 px-3 border-0",
                                                listeners: {
                                                    click: () =>
                                                        this.addDealProduct(
                                                            prod,
                                                        ),
                                                },
                                            },
                                            h.img({
                                                src:
                                                    prod.thumbnail_path ||
                                                    "/assets/images/product-placeholder.png",
                                                class: "rounded",
                                                style: "width: 30px; height: 30px; object-fit: cover;",
                                            }),
                                            h.div(
                                                { class: "small fw-bold" },
                                                prod.name,
                                            ),
                                        ),
                                    ),
                                );
                            });
                        }),
                    );
                }),
            ),

            v(this.dealProducts, (productsState) =>
                h.div(
                    { class: "d-grid gap-2 mb-4" },
                    productsState.value.length === 0
                        ? h.div(
                              {
                                  class: "p-4 bg-light rounded-4 text-center text-muted border-dashed small",
                              },
                              "No products added to this deal yet.",
                          )
                        : productsState.value.map((p, i) =>
                              this.renderDealProductItem(p, i),
                          ),
                ),
            ),
        );
    }

    renderDealProductItem(product, index) {
        return h.div(
            {
                class: "d-flex align-items-center gap-3 p-2 bg-light rounded-3",
            },
            h.img({
                src:
                    product.thumbnail_path ||
                    "/assets/images/product-placeholder.png",
                class: "rounded-3",
                style: "width: 44px; height: 44px; object-fit: cover;",
            }),
            h.div(
                { class: "flex-fill" },
                h.div({ class: "fw-bold small" }, product.name),
                product.selling_price != null
                    ? h.div(
                          { class: "text-muted small" },
                          monetary(product.selling_price, dc.userCurrency),
                      )
                    : null,
            ),
            h.button(
                {
                    class: "btn btn-white btn-sm rounded-circle border text-danger",
                    listeners: {
                        click: () => {
                            updateStateValue(this.dealProducts, (v) => {
                                v.splice(index, 1);
                                return v;
                            });
                        },
                    },
                },
                h.i({ class: "bi bi-x-lg" }),
            ),
        );
    }

    handleDealProductSearch(e) {
        const q = e.target.value.trim();
        if (this.productSearchTimeout) clearTimeout(this.productSearchTimeout);
        if (q.length < 2) {
            this.productSuggestions.value = [];
            this.showProductSuggestions.value = false;
            return;
        }
        this.productSearchTimeout = setTimeout(
            () => this.fetchDealProductSuggestions(q),
            300,
        );
    }

    async fetchDealProductSuggestions(query) {
        this.loadingProductSuggestions.value = true;
        this.showProductSuggestions.value = true;
        try {
            const res = await api.get(
                routes.companyProductsSearch(this.companyId),
                { q: query },
            );
            if (res)
                this.productSuggestions.value = res.data.result.data || [];
        } catch (e) {
            consoleErrorOnLocal(e);
        } finally {
            this.loadingProductSuggestions.value = false;
        }
    }

    addDealProduct(prod) {
        const exists = this.dealProducts.value.find((p) => p.id === prod.id);
        if (!exists) {
            updateStateValue(this.dealProducts, (v) => {
                v.push({
                    id: prod.id,
                    name: prod.name,
                    thumbnail_path: prod.thumbnail_path,
                    selling_price: prod.selling_price,
                });
                return v;
            });
        }
        this.showProductSuggestions.value = false;
        const input = dom.get("#deal-product-search");
        if (input) input.value = "";
    }

    async saveDeal(btn) {
        const name = (this.formData.name || "").trim();
        const value = parseFloat(this.formData.value);

        if (!name) {
            inform("Please enter a deal name.", STRINGS.ERROR);
            return;
        }
        if (!value || value <= 0) {
            inform("Please enter a value greater than 0.", STRINGS.ERROR);
            return;
        }
        if (this.formData.type === "percentage" && value > 100) {
            inform(
                "Percentage discount cannot exceed 100.",
                STRINGS.ERROR,
            );
            return;
        }
        if (this.formData.starts_at && this.formData.ends_at) {
            if (
                new Date(this.formData.ends_at) <
                new Date(this.formData.starts_at)
            ) {
                inform(
                    "End date must be on or after the start date.",
                    STRINGS.ERROR,
                );
                return;
            }
        }

        dom.disable(btn);
        h.SmallSpinner({ parent: btn, resetParent: true });

        const body = {
            name,
            type: this.formData.type,
            value,
            active: this.formData.active,
        };
        if (this.formData.starts_at) body.starts_at = this.formData.starts_at;
        if (this.formData.ends_at) body.ends_at = this.formData.ends_at;

        try {
            const res = this.editingDealId
                ? await api.put(
                      routes.companyDeal(this.companyId, this.editingDealId),
                      body,
                  )
                : await api.post(routes.companyDeals(this.companyId), body);

            if (res) {
                const deal = res.data.deal;
                const productIds = this.dealProducts.value.map((p) => p.id);

                const productsRes = await api.put(
                    routes.companyDealProducts(this.companyId, deal.id),
                    { product_ids: productIds },
                );

                if (productsRes) {
                    inform(
                        this.editingDealId
                            ? "Deal updated!"
                            : "Deal created!",
                        STRINGS.SUCCESS,
                    );
                    this.dealFormHolder.hide();
                    this.fetchDeals();
                }
            }
        } catch (e) {
            consoleErrorOnLocal(e);
            inform("Failed to save deal.", STRINGS.ERROR);
        } finally {
            dom.enable(btn);
            h.span(this.editingDealId ? "Update Deal" : "Create Deal", {
                parent: btn,
                resetParent: true,
            });
        }
    }

    deleteDeal(deal) {
        broker.send(
            $e.deal.needs.deletion,
            payload({ id: deal.id, businessId: this.companyId }),
        );
    }

    // =====================================================================
    // DATA FETCHING
    // =====================================================================

    async fetchDeals() {
        this.loadingDeals.value = true;
        try {
            if (!this.companyId) return;

            const res = await api.get(routes.companyDeals(this.companyId));

            this.loadingDeals.value = false;
            if (res) {
                this.deals.value = res.data.deals || [];
            }
        } catch (e) {
            consoleErrorOnLocal(e);
            this.loadingDeals.value = false;
        }
    }

    // =====================================================================
    // HELPERS
    // =====================================================================

    isDealCurrentlyActive(deal) {
        if (!deal.active) return false;
        const now = new Date();
        if (deal.starts_at && new Date(deal.starts_at) > now) return false;
        if (deal.ends_at && new Date(deal.ends_at) < now) return false;
        return true;
    }

    filterDeals(deals, filters) {
        return deals.filter((deal) => {
            const currentlyActive = this.isDealCurrentlyActive(deal);
            if (filters.status === "active" && !currentlyActive) return false;
            if (filters.status === "inactive" && currentlyActive) return false;

            if (filters.startDate || filters.endDate) {
                const rangeStart = filters.startDate
                    ? new Date(filters.startDate)
                    : null;
                const rangeEnd = filters.endDate
                    ? new Date(filters.endDate)
                    : null;
                const dealStart = deal.starts_at
                    ? new Date(deal.starts_at)
                    : null;
                const dealEnd = deal.ends_at ? new Date(deal.ends_at) : null;

                // Deal window must overlap the selected [rangeStart, rangeEnd] range.
                if (rangeEnd && dealStart && dealStart > rangeEnd)
                    return false;
                if (rangeStart && dealEnd && dealEnd < rangeStart)
                    return false;
            }

            return true;
        });
    }

    formatDealValue(deal) {
        if (deal.type === "percentage") {
            return `${Number(deal.value)}% off`;
        }
        return `${monetary(deal.value, dc.userCurrency)} off`;
    }

    humanDealDate(isoString) {
        return formatDateForHumans(isoString).replace(
            /\s+\d{1,2}:\d{2}\s?[AP]M$/i,
            "",
        );
    }

    formatDealWindow(deal) {
        if (!deal.starts_at && !deal.ends_at) return "Always active";
        const start = deal.starts_at
            ? this.humanDealDate(deal.starts_at)
            : "Any time";
        const end = deal.ends_at
            ? this.humanDealDate(deal.ends_at)
            : "No end date";
        return `${start} → ${end}`;
    }

    toDateInputValue(isoString) {
        if (!isoString) return "";
        return new Date(isoString).toISOString().split("T")[0];
    }

    cleanUp() {
        if (this.dealFormHolder) cleanDispose(this.dealFormHolder);
        super.cleanUp();
    }
}

ojs(ViewDeals);
